const rawApiBaseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";
const normalizedApiBaseUrl = rawApiBaseUrl.replace(/\/+$/, "");
const API_BASE_URL = normalizedApiBaseUrl.endsWith("/api")
  ? normalizedApiBaseUrl
  : `${normalizedApiBaseUrl}/api`;
const SERVER_BASE_URL = API_BASE_URL.replace(/\/api$/, "");

const AUTH_SESSION_STORAGE_KEY = "puck-pickup-auth-session";

/** Fired on the window when the server says the signed-in person's session has ended (HTTP 401). */
export const SESSION_EXPIRED_EVENT = "puck-pickup-session-expired";

/**
 * Thrown for a request that failed because the session ended, as opposed to some other problem. `message` is
 * still the server's own wording (a few screens match on it); check `code` or use isSessionExpiredError().
 */
export class SessionExpiredError extends Error {
  constructor(message) {
    super(message || "Your session expired. Please sign in again.");
    this.name = "SessionExpiredError";
    this.code = "SESSION_EXPIRED";
    this.status = 401;
  }
}

/** True if someone has signed in on this browser before, even if their session has since run out. */
const hasStoredSession = () => {
  try {
    return Boolean(localStorage.getItem(AUTH_SESSION_STORAGE_KEY));
  } catch {
    return false;
  }
};

const dropStoredSession = () => {
  try {
    localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
};

// Paths where a 401 means "that sign-in attempt didn't work", not "your session ended".
const SIGN_IN_PATHS = ["/auth/google/sign-in"];

const getStoredAccessToken = () => {
  try {
    const raw = localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    const expiresAt = session?.expiresAt ?? 0;
    const nowSeconds = Math.floor(Date.now() / 1000);
    if (expiresAt - 30 <= nowSeconds) return null;
    return session?.accessToken ?? null;
  } catch {
    return null;
  }
};

async function request(path, options = {}) {
  const token = getStoredAccessToken();
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  // Whether this person was signed in (or had been) when the request went out.
  const hadSession = Boolean(token) || hasStoredSession();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  // The server says 401 only when it couldn't identify the caller. For someone who was signed in, that means
  // their session ended (timed out, or was cleared). Say so, rather than leaving each screen to guess from a
  // generic failure. Anonymous visitors hitting a protected page are not "expired", so they are left alone.
  if (response.status === 401 && hadSession && !SIGN_IN_PATHS.includes(path)) {
    let serverMessage = "";
    try {
      serverMessage = (await response.json())?.error?.message || "";
    } catch {
      // Not JSON; the default wording is fine.
    }
    // The stored session is no good any more, so drop it. The nav bar then shows "Sign in" instead of
    // looking signed in while every request fails.
    dropStoredSession();
    window.dispatchEvent(new Event("puck-pickup-auth-change"));
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    throw new SessionExpiredError(serverMessage);
  }

  let payload;
  try {
    payload = await response.json();
  } catch {
    throw new Error(response.status === 400 ? "Bad request" : "Request failed");
  }

  if (!response.ok || !payload.ok) {
    const message = payload?.error?.message || "Request failed";
    const details = payload?.error?.details;
    const zodMessage =
      details?.issues?.length > 0 ? details.issues.map((i) => i.message).join("; ") : null;
    throw new Error(zodMessage ? `${message}: ${zodMessage}` : message);
  }

  return payload.data;
}

export { API_BASE_URL, SERVER_BASE_URL, request, getStoredAccessToken };
